import type { SchedulingTimezone } from "@whitehaven/contracts";
import type { AuthorizationSubject } from "@whitehaven/permissions";
import { useEffect, type ReactNode } from "react";
import {
  ADMIN_CONVERSATION_INBOX_PATH,
  AdminConversationInboxRoute,
} from "./features/chat/AdminConversationInboxRoute";
import type { AdminConversationInboxGateways } from "./features/chat/admin-conversation-gateway";
import {
  ADMIN_HOMEPAGE_CONTENT_PATH,
  isLegacyAdminHomepageContentPath,
} from "./features/content/homepage-editor-route-model.ts";
import { AdminLeadDetailRoute } from "./features/crm/AdminLeadDetailRoute";
import type { AdminLeadListGateway } from "./features/crm/lead-list-gateway";
import type { AdminLeadDetailGateway } from "./features/crm/lead-detail-gateway";
import type { AdminLeadDuplicateGateway } from "./features/crm/lead-duplicate-gateway";
import type { AdminLeadEngagementGateways } from "./features/crm/lead-engagement-gateway";
import type { AdminLeadGuestConversionMutationGateway } from "./features/crm/lead-guest-conversion-gateway";
import type { AdminLeadAppointmentConversionGateways } from "./features/crm/lead-appointment-conversion-gateway";
import type { AdminLeadStageHistoryGateway } from "./features/crm/lead-stage-history-gateway";
import type {
  AdminLeadAssigneeDirectoryGateway,
  AdminLeadAssignmentMutationGateway,
} from "./features/crm/lead-assignment-gateway";
import type {
  AdminLeadPipelineMutationGateway,
  AdminLeadPipelineReadGateway,
} from "./features/crm/lead-pipeline-gateway";
import { AdminLeadPipelineRoute } from "./features/crm/AdminLeadPipelineRoute";
import { resolveAdminCrmRoute } from "./features/crm/lead-detail-route-model";
import { AdminLeadListRoute } from "./features/crm/AdminLeadListRoute";
import { AdminSchedulingRoute } from "./features/scheduling/AdminSchedulingRoute";
import type {
  AdminSchedulingCatalogMutationGateway,
  AdminSchedulingCatalogReadGateway,
  AdminSchedulingWorkspaceMutationGateway,
  AdminSchedulingWorkspaceReadGateway,
} from "./features/scheduling/scheduling-gateway";
import { resolveAdminSchedulingRoute } from "./features/scheduling/scheduling-portal-model";
import { PortalHomeRoute } from "./PortalHomeRoute";
import { StaffOperationsRoute } from "./features/operations/StaffOperationsRoute";
import type {
  StaffBarcodeControlGateway,
  StaffOperationsMutationGateway,
  StaffOperationsReadGateway,
  StaffPrintDialogAuditGateway,
  StaffVisitPacketGateway,
} from "./features/operations/staff-operations-gateway";
import { resolveStaffOperationsRoute } from "./features/operations/staff-operations-model";
import { AdminConnectorSettingsRoute } from "./features/settings/AdminConnectorSettingsRoute";
import type { AdminConnectorSettingsGateways } from "./features/settings/admin-connector-settings-gateway";
import { resolveAdminConnectorSettingsRoute } from "./features/settings/admin-connector-settings-model";
import { SiteProvisioningRoute } from "./features/site-setup/SiteProvisioningRoute";

interface PortalRouteOutletProps {
  readonly pathname: string;
  readonly subject: AuthorizationSubject | null;
  readonly adminLeadListGateway: AdminLeadListGateway | null;
  readonly adminLeadDetailGateway?: AdminLeadDetailGateway | null;
  readonly adminLeadDuplicateGateway?: AdminLeadDuplicateGateway | null;
  readonly adminLeadStageHistoryGateway?: AdminLeadStageHistoryGateway | null;
  readonly adminLeadAssigneeDirectoryGateway?: AdminLeadAssigneeDirectoryGateway | null;
  readonly adminLeadAssignmentMutationGateway?: AdminLeadAssignmentMutationGateway | null;
  readonly adminLeadEngagementGateways?: AdminLeadEngagementGateways | null;
  readonly adminLeadGuestConversionMutationGateway?: AdminLeadGuestConversionMutationGateway | null;
  readonly adminLeadAppointmentConversionGateways?: AdminLeadAppointmentConversionGateways | null;
  readonly adminLeadPipelineReadGateway?: AdminLeadPipelineReadGateway | null;
  readonly adminLeadPipelineMutationGateway?: AdminLeadPipelineMutationGateway | null;
  readonly adminConversationInboxGateways?: AdminConversationInboxGateways | null;
  readonly adminSchedulingCatalogReadGateway?: AdminSchedulingCatalogReadGateway | null;
  readonly adminSchedulingCatalogMutationGateway?: AdminSchedulingCatalogMutationGateway | null;
  readonly adminSchedulingWorkspaceReadGateway?: AdminSchedulingWorkspaceReadGateway | null;
  readonly adminSchedulingWorkspaceMutationGateway?: AdminSchedulingWorkspaceMutationGateway | null;
  readonly adminSchedulingAnchorDate?: string | undefined;
  readonly adminConnectorSettingsGateways?: AdminConnectorSettingsGateways | null;
  readonly staffOperationsReadGateway?: StaffOperationsReadGateway | null;
  readonly staffOperationsMutationGateway?: StaffOperationsMutationGateway | null;
  readonly staffVisitPacketGateway?: StaffVisitPacketGateway | null;
  readonly staffPrintDialogAuditGateway?: StaffPrintDialogAuditGateway | null;
  readonly staffBarcodeControlGateway?: StaffBarcodeControlGateway | null;
  readonly staffPrinterProfileId?: string | null;
  readonly staffOperationsSyntheticPreview?: boolean;
  readonly staffOperationsDate?: string | undefined;
  readonly staffOperationsLocationTimezones?:
    | Readonly<Record<string, SchedulingTimezone | undefined>>
    | undefined;
  readonly homepageEditorRoute?: ReactNode;
  readonly roleAccessRoute?: ReactNode;
  readonly fallbackRoute?: ReactNode;
}

const noStaffOperationsLocationTimezones = Object.freeze({}) as Readonly<
  Record<string, SchedulingTimezone | undefined>
>;

function LegacyHomepageRouteRedirect() {
  useEffect(() => {
    window.location.replace(ADMIN_HOMEPAGE_CONTENT_PATH);
  }, []);

  return (
    <p className="portal-route-redirect" role="status" aria-live="polite">
      Opening the current V2.1 homepage editor…
    </p>
  );
}

function currentHonoluluDate() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Pacific/Honolulu",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

export function isAdminLeadListRoute(pathname: string) {
  return resolveAdminCrmRoute(pathname)?.kind === "lead-list";
}

export function isAdminLeadDetailRoute(pathname: string) {
  return resolveAdminCrmRoute(pathname)?.kind === "lead-detail";
}

export function isAdminLeadPipelineRoute(pathname: string) {
  return resolveAdminCrmRoute(pathname)?.kind === "lead-pipeline";
}

export function PortalRouteOutlet({
  pathname,
  subject,
  adminLeadListGateway,
  adminLeadDetailGateway = null,
  adminLeadDuplicateGateway = null,
  adminLeadStageHistoryGateway = null,
  adminLeadAssigneeDirectoryGateway = null,
  adminLeadAssignmentMutationGateway = null,
  adminLeadEngagementGateways = null,
  adminLeadGuestConversionMutationGateway = null,
  adminLeadAppointmentConversionGateways = null,
  adminLeadPipelineReadGateway = null,
  adminLeadPipelineMutationGateway = null,
  adminConversationInboxGateways = null,
  adminSchedulingCatalogReadGateway = null,
  adminSchedulingCatalogMutationGateway = null,
  adminSchedulingWorkspaceReadGateway = null,
  adminSchedulingWorkspaceMutationGateway = null,
  adminSchedulingAnchorDate,
  adminConnectorSettingsGateways = null,
  staffOperationsReadGateway = null,
  staffOperationsMutationGateway = null,
  staffVisitPacketGateway = null,
  staffPrintDialogAuditGateway = null,
  staffBarcodeControlGateway = null,
  staffPrinterProfileId = null,
  staffOperationsSyntheticPreview = false,
  staffOperationsDate,
  staffOperationsLocationTimezones = noStaffOperationsLocationTimezones,
  homepageEditorRoute = null,
  roleAccessRoute = null,
  fallbackRoute,
}: PortalRouteOutletProps) {
  const crmRoute = resolveAdminCrmRoute(pathname);
  const schedulingRoute = resolveAdminSchedulingRoute(pathname);
  const staffRoute = resolveStaffOperationsRoute(pathname);
  const connectorSettingsRoute = resolveAdminConnectorSettingsRoute(pathname);

  if (
    pathname === ADMIN_HOMEPAGE_CONTENT_PATH &&
    homepageEditorRoute !== null
  ) {
    return homepageEditorRoute;
  }

  if (isLegacyAdminHomepageContentPath(pathname)) {
    return <LegacyHomepageRouteRedirect />;
  }

  if (pathname === "/admin/roles" && roleAccessRoute !== null) {
    return roleAccessRoute;
  }

  if (pathname === "/admin/site-setup") {
    return <SiteProvisioningRoute subject={subject} />;
  }

  if (staffRoute?.kind === "operations") {
    return (
      <StaffOperationsRoute
        key={`${staffRoute.view}:${staffRoute.appointmentId ?? ""}:${staffRoute.visitId ?? ""}`}
        subject={subject}
        view={staffRoute.view}
        initialAppointmentId={staffRoute.appointmentId ?? null}
        initialVisitId={staffRoute.visitId ?? null}
        date={staffOperationsDate ?? currentHonoluluDate()}
        locationTimezones={staffOperationsLocationTimezones}
        readGateway={staffOperationsReadGateway}
        mutationGateway={staffOperationsMutationGateway}
        packetGateway={staffVisitPacketGateway}
        printDialogAuditGateway={staffPrintDialogAuditGateway}
        barcodeControlGateway={staffBarcodeControlGateway}
        printerProfileId={staffPrinterProfileId}
        syntheticPreview={staffOperationsSyntheticPreview}
      />
    );
  }

  if (pathname === ADMIN_CONVERSATION_INBOX_PATH) {
    return (
      <AdminConversationInboxRoute
        subject={subject}
        gateways={adminConversationInboxGateways}
      />
    );
  }

  if (connectorSettingsRoute) {
    return (
      <AdminConnectorSettingsRoute
        pathname={pathname}
        subject={subject}
        gateways={adminConnectorSettingsGateways}
      />
    );
  }

  if (crmRoute?.kind === "lead-list") {
    return (
      <AdminLeadListRoute subject={subject} gateway={adminLeadListGateway} />
    );
  }

  if (crmRoute?.kind === "lead-detail") {
    return (
      <AdminLeadDetailRoute
        subject={subject}
        leadId={crmRoute.leadId}
        gateway={adminLeadDetailGateway}
        duplicateGateway={adminLeadDuplicateGateway}
        stageHistoryGateway={adminLeadStageHistoryGateway}
        assigneeDirectoryGateway={adminLeadAssigneeDirectoryGateway}
        assignmentMutationGateway={adminLeadAssignmentMutationGateway}
        engagementGateways={adminLeadEngagementGateways}
        guestConversionMutationGateway={adminLeadGuestConversionMutationGateway}
        appointmentConversionGateways={adminLeadAppointmentConversionGateways}
      />
    );
  }

  if (crmRoute?.kind === "lead-pipeline") {
    return (
      <AdminLeadPipelineRoute
        subject={subject}
        readGateway={adminLeadPipelineReadGateway}
        mutationGateway={adminLeadPipelineMutationGateway}
      />
    );
  }

  if (crmRoute?.kind === "invalid") {
    return (
      <AdminLeadDetailRoute
        subject={subject}
        leadId={null}
        gateway={adminLeadDetailGateway}
        duplicateGateway={adminLeadDuplicateGateway}
        stageHistoryGateway={adminLeadStageHistoryGateway}
        assigneeDirectoryGateway={adminLeadAssigneeDirectoryGateway}
        assignmentMutationGateway={adminLeadAssignmentMutationGateway}
        engagementGateways={adminLeadEngagementGateways}
        guestConversionMutationGateway={adminLeadGuestConversionMutationGateway}
        appointmentConversionGateways={adminLeadAppointmentConversionGateways}
      />
    );
  }

  if (schedulingRoute) {
    return (
      <AdminSchedulingRoute
        pathname={pathname}
        anchorDate={adminSchedulingAnchorDate ?? currentHonoluluDate()}
        subject={subject}
        catalogReadGateway={adminSchedulingCatalogReadGateway}
        catalogMutationGateway={adminSchedulingCatalogMutationGateway}
        workspaceReadGateway={adminSchedulingWorkspaceReadGateway}
        workspaceMutationGateway={adminSchedulingWorkspaceMutationGateway}
      />
    );
  }

  return fallbackRoute ?? <PortalHomeRoute />;
}
